'use client';
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { getDisplayName } from '@/lib/displayName';
import SpecialistCard from '@/components/specialists/SpecialistCard';

interface Props {
  specialists: any[];
  initialDate?: string;
}

const AUTOPLAY_INTERVAL = 4000;
const INTERACTION_PAUSE = 10000;

const getAvatarColor = (name: string) => {
  const colors = ['#307479', '#7c3aed', '#2563eb', '#e11d48', '#d97706', '#059669', '#4f46e5', '#0891b2'];
  return colors[name.charCodeAt(0) % colors.length];
};

const apiBase = process.env.NEXT_PUBLIC_API_URL?.replace('/api/v1', '') ?? '';
const resolveUrl = (url: string) => (url?.startsWith('http') ? url : apiBase + url);

/* ══ Карусель портфолио активной карточки ══ */
function PortfolioCarousel({ specialist }: { specialist: any }) {
  const displayName = getDisplayName(specialist.user);
  const photos: string[] = useMemo(() => {
    const items = (specialist.portfolioItems ?? []) as { imageUrl: string }[];
    return items.map(p => resolveUrl(p.imageUrl)).filter(Boolean);
  }, [specialist]);

  const [activeIdx, setActiveIdx] = useState(0);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const pausedUntilRef = useRef<number>(0);
  const autoplayTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Сбрасываем индекс при смене специалиста
  useEffect(() => {
    setActiveIdx(0);
    if (scrollerRef.current) scrollerRef.current.scrollTo({ left: 0 });
  }, [specialist.id]);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  }, []);

  useEffect(() => {
    if (photos.length <= 1 || prefersReducedMotion) return;
    autoplayTimerRef.current = setInterval(() => {
      if (Date.now() < pausedUntilRef.current) return;
      const el = scrollerRef.current;
      if (!el) return;
      const width = el.clientWidth;
      const nextIdx = (activeIdx + 1) % photos.length;
      el.scrollTo({ left: nextIdx * width, behavior: 'smooth' });
    }, AUTOPLAY_INTERVAL);
    return () => { if (autoplayTimerRef.current) clearInterval(autoplayTimerRef.current); };
  }, [photos.length, activeIdx, prefersReducedMotion]);

  const handleTouchStart = useCallback(() => {
    pausedUntilRef.current = Date.now() + INTERACTION_PAUSE;
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || el.clientWidth === 0) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    setActiveIdx(prev => (prev === idx ? prev : idx));
  }, []);

  if (photos.length === 0) {
    return (
      <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden flex items-center justify-center"
        style={{ background: getAvatarColor(displayName) }}>
        <span className="text-white font-bold text-5xl">{displayName.charAt(0).toUpperCase()}</span>
      </div>
    );
  }

  return (
    <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden">
      <div
        ref={scrollerRef}
        onTouchStart={handleTouchStart}
        onScroll={handleScroll}
        className="flex w-full h-full overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {photos.map((src, i) => (
          <img
            key={i}
            src={src}
            alt={`${displayName} ${i + 1}`}
            className="w-full h-full object-cover flex-shrink-0 snap-center"
            style={{ minWidth: '100%' }}
          />
        ))}
      </div>
      {photos.length > 1 && (
        <div className="absolute bottom-2 inset-x-0 flex items-center justify-center gap-1.5">
          {photos.map((_, i) => (
            <span
              key={i}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === activeIdx ? 16 : 6,
                background: i === activeIdx ? '#ffffff' : 'rgba(255,255,255,0.5)',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ══ Мини-карточка для нижней карусели ══ */
function MiniCard({ specialist, onSelect }: { specialist: any; onSelect: () => void }) {
  const displayName = getDisplayName(specialist.user);
  const photoUrl = specialist.user?.avatarUrl
    ? resolveUrl(specialist.user.avatarUrl)
    : (specialist.portfolioItems ?? [])[0]?.imageUrl
      ? resolveUrl(specialist.portfolioItems[0].imageUrl)
      : null;

  const minPrice = (specialist.services ?? []).reduce((min: number | null, s: any) => {
    const p = Number(s.price);
    if (isNaN(p)) return min;
    return min === null ? p : Math.min(min, p);
  }, null as number | null);

  const nextSlotLabel = useMemo(() => {
    const nextSlotAt = specialist.nextSlotAt;
    if (!nextSlotAt) return null;
    const d = new Date(nextSlotAt);
    if (isNaN(d.getTime()) || d < new Date()) return null;
    const tz = specialist.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const todayStr = new Date().toLocaleDateString('sv-SE', { timeZone: tz });
    const slotStr = d.toLocaleDateString('sv-SE', { timeZone: tz });
    const timeStr = d.toLocaleTimeString('ru-RU', { timeZone: tz, hour: '2-digit', minute: '2-digit' });
    if (slotStr === todayStr) return `Сегодня, ${timeStr}`;
    return d.toLocaleDateString('ru-RU', { timeZone: tz, day: 'numeric', month: 'short' }) + ', ' + timeStr;
  }, [specialist.nextSlotAt, specialist.timezone]);

  return (
    <button
      type="button"
      onClick={onSelect}
      className="relative flex-shrink-0 overflow-hidden rounded-xl snap-start text-left border border-border bg-bg-surface transition-transform duration-200 active:scale-95"
      style={{ width: 140, height: 170, minWidth: 44, minHeight: 44 }}
      aria-label={`Показать ${displayName}`}
    >
      {photoUrl ? (
        <img src={photoUrl} alt={displayName} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center" style={{ background: getAvatarColor(displayName) }}>
          <span className="text-white font-bold text-2xl">{displayName.charAt(0).toUpperCase()}</span>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 p-2 text-white">
        <div className="text-xs font-semibold truncate drop-shadow">{displayName}</div>
        <div className="flex items-center gap-1.5 text-[11px] mt-0.5 flex-wrap">
          {specialist.rating > 0 && <span className="font-medium">★ {Number(specialist.rating).toFixed(1)}</span>}
        </div>
        {minPrice !== null && (
          <div className="text-[11px] font-semibold mt-0.5">от {minPrice.toLocaleString('ru-RU')} ₽</div>
        )}
        <div className="text-[10px] opacity-90 truncate mt-0.5">
          {nextSlotLabel || specialist.city || ''}
        </div>
      </div>
    </button>
  );
}

/* ══ ГЛАВНЫЙ КОМПОНЕНТ ══ */
export default function MobileFocusView({ specialists, initialDate }: Props) {
  const [activeId, setActiveId] = useState<string | null>(specialists[0]?.id ?? null);
  const [transitioning, setTransitioning] = useState(false);

  // Если активный специалист исчез из списка (фильтры/пагинация) — сбрасываем на первого
  useEffect(() => {
    if (specialists.length === 0) { setActiveId(null); return; }
    if (!specialists.some(s => s.id === activeId)) {
      setActiveId(specialists[0].id);
    }
  }, [specialists, activeId]);

  const active = specialists.find(s => s.id === activeId) ?? specialists[0] ?? null;
  const others = specialists.filter(s => s.id !== active?.id);

  const handleSelect = useCallback((id: string) => {
    if (id === activeId) return;
    setTransitioning(true);
    setActiveId(id);
    window.setTimeout(() => setTransitioning(false), 200);
  }, [activeId]);

  if (!active) return null;

  return (
    <div className="flex flex-col gap-4">
      {/* Активная карточка */}
      <div
        key={active.id}
        className="transition-all duration-200"
        style={{
          opacity: transitioning ? 0.4 : 1,
          transform: transitioning ? 'scale(0.98)' : 'scale(1)',
        }}
      >
        <div className="mb-3">
          <PortfolioCarousel specialist={active} />
        </div>
        <SpecialistCard specialist={active} initialDate={initialDate} />
      </div>

      {/* Карусель остальных специалистов */}
      {others.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-text-muted mb-2 px-0.5">Другие специалисты</div>
          <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {others.map(s => (
              <MiniCard key={s.id} specialist={s} onSelect={() => handleSelect(s.id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
